import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Mic, Square, Volume2, Check, AlertCircle, RotateCcw, Loader2 } from "lucide-react";

// -----------------------------------------------------------------------
// VoiceRecorder — MediaRecorder → /api/speak/transcribe (Whisper)
//
// Non-diagnostic. Audio is captured on-device with `getUserMedia`, streamed
// once into a Blob, POSTed to the backend, and the file is discarded after
// transcription. Returns { transcript, similarity 0-100, clarity, tip }.
// -----------------------------------------------------------------------

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const MAX_MS = 8000;
const MIN_MS = 400;

// Map our language code to a BCP-47 tag speechSynthesis prefers.
const TTS_LANG = { en: "en-IN", ta: "ta-IN", hi: "hi-IN" };

const speakTarget = (text, lang) => {
  if (!text || typeof window === "undefined" || !window.speechSynthesis) return false;
  try {
    window.speechSynthesis.cancel();
    const u = new window.SpeechSynthesisUtterance(text);
    u.lang = TTS_LANG[lang] || "en-IN";
    u.rate = 0.85;
    // Pick a voice matching the language if the browser exposes one.
    const voices = window.speechSynthesis.getVoices();
    const match = voices.find((v) => v.lang && v.lang.toLowerCase().startsWith((TTS_LANG[lang] || "en").toLowerCase()));
    if (match) u.voice = match;
    window.speechSynthesis.speak(u);
    return true;
  } catch {
    return false;
  }
};

export default function VoiceRecorder({
  target = "",
  language = "en",
  onResult,
  maxMs = MAX_MS,
  ctaText = "Tap to Speak",
  testId = "speak-recorder",
}) {
  const [state, setState] = useState("idle"); // idle | recording | uploading | result | error
  const [error, setError] = useState(null);
  const [elapsedMs, setElapsedMs] = useState(0);
  const [supported] = useState(
    typeof window !== "undefined" &&
      !!(navigator.mediaDevices && window.MediaRecorder)
  );
  const [result, setResult] = useState(null);
  const recorderRef = useRef(null);
  const chunksRef = useRef([]);
  const startedAtRef = useRef(0);
  const tickRef = useRef(null);

  useEffect(() => () => cleanup(), []);

  const cleanup = () => {
    try {
      if (tickRef.current) clearInterval(tickRef.current);
      if (recorderRef.current && recorderRef.current.state !== "inactive") {
        recorderRef.current.stop();
      }
      recorderRef.current?.stream?.getTracks?.().forEach((t) => t.stop());
    } catch {
      /* ignore */
    }
  };

  const start = async () => {
    if (!supported) {
      setError("Voice recording isn't supported on this device or browser.");
      setState("error");
      return;
    }
    setError(null);
    setResult(null);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mime =
        window.MediaRecorder.isTypeSupported &&
        window.MediaRecorder.isTypeSupported("audio/webm;codecs=opus")
          ? "audio/webm;codecs=opus"
          : window.MediaRecorder.isTypeSupported &&
            window.MediaRecorder.isTypeSupported("audio/webm")
          ? "audio/webm"
          : "";
      const rec = mime
        ? new MediaRecorder(stream, { mimeType: mime })
        : new MediaRecorder(stream);
      chunksRef.current = [];
      rec.ondataavailable = (e) => {
        if (e.data && e.data.size) chunksRef.current.push(e.data);
      };
      rec.onstop = async () => {
        const blob = new Blob(chunksRef.current, {
          type: chunksRef.current[0]?.type || "audio/webm",
        });
        rec.stream.getTracks().forEach((t) => t.stop());
        if (blob.size < 800) {
          setError("Recording was too short. Hold the button a bit longer.");
          setState("error");
          return;
        }
        upload(blob);
      };
      recorderRef.current = rec;
      startedAtRef.current = performance.now();
      setElapsedMs(0);
      setState("recording");
      rec.start();
      tickRef.current = setInterval(() => {
        const e = performance.now() - startedAtRef.current;
        setElapsedMs(e);
        if (e >= maxMs) stop();
      }, 100);
    } catch (e) {
      const denied = /permission|not allowed|denied/i.test(String(e?.message || e));
      setError(
        denied
          ? "Microphone access was denied. Please allow the mic and try again."
          : "Unable to access the microphone."
      );
      setState("error");
    }
  };

  const stop = () => {
    if (tickRef.current) clearInterval(tickRef.current);
    tickRef.current = null;
    const dur = performance.now() - startedAtRef.current;
    if (dur < MIN_MS) {
      // Wait a bit and then stop, otherwise MediaRecorder may not have data yet.
      setTimeout(() => {
        try {
          recorderRef.current?.stop();
        } catch {
          /* ignore */
        }
      }, MIN_MS - dur + 50);
    } else {
      try {
        recorderRef.current?.stop();
      } catch {
        /* ignore */
      }
    }
  };

  const upload = async (blob) => {
    setState("uploading");
    try {
      const fd = new FormData();
      const ext = blob.type.includes("webm") ? "webm" : "wav";
      fd.append("audio", blob, `clip.${ext}`);
      fd.append("target", target || "");
      fd.append("language", language || "en");
      const res = await fetch(`${BACKEND}/api/speak/transcribe`, {
        method: "POST",
        body: fd,
      });
      if (!res.ok) {
        const t = await res.text();
        throw new Error(t || `HTTP ${res.status}`);
      }
      const data = await res.json();
      setResult(data);
      setState("result");
      onResult?.(data);
    } catch (e) {
      setError("Transcription service is unavailable. Please try again.");
      setState("error");
    }
  };

  const reset = () => {
    setResult(null);
    setError(null);
    setState("idle");
  };

  const percentBar = Math.min(100, Math.round((elapsedMs / maxMs) * 100));
  const secs = (elapsedMs / 1000).toFixed(1);

  return (
    <div data-testid={testId} className="rounded-3xl border border-cyan-400/25 bg-cyan-400/[0.05] p-5">
      <div className="flex items-baseline justify-between gap-3 flex-wrap">
        <div className="text-[10px] uppercase tracking-widest text-cyan-300 font-semibold">
          Voice practice · Powered by Whisper AI
        </div>
        {target && (
          <div className="text-[10px] uppercase tracking-widest text-zinc-400 font-semibold">
            Look at the picture and say:{" "}
            <span className="font-display text-lg text-white normal-case tracking-normal">
              {target}
            </span>
          </div>
        )}
      </div>

      {!supported && (
        <div className="mt-3 rounded-2xl border border-amber-500/30 bg-amber-500/10 p-3 text-xs text-amber-100">
          Voice recording isn&apos;t supported in this browser. Please open the site in Chrome / Safari on a device with a microphone.
        </div>
      )}

      {/* Controls */}
      <div className="mt-3 flex flex-wrap items-center gap-2">
        {state === "idle" && (
          <>
            <button
              data-testid={`${testId}-start`}
              onClick={start}
              disabled={!supported}
              className="inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-cyan-400 to-emerald-400 text-black font-semibold px-6 py-3 text-base hover:from-cyan-300 hover:to-emerald-300 disabled:opacity-50 shadow-[0_0_30px_-8px_rgba(34,211,238,0.6)]"
            >
              <Mic className="w-5 h-5" /> {ctaText}
            </button>
            {target && (
              <button
                data-testid={`${testId}-hear`}
                onClick={() => speakTarget(target, language)}
                title={`Hear "${target}"`}
                className="inline-flex items-center gap-1.5 rounded-full border border-white/15 bg-white/5 hover:bg-white/10 text-white px-4 py-3 text-sm"
              >
                <Volume2 className="w-4 h-4 text-cyan-300" /> Hear the word
              </button>
            )}
          </>
        )}
        {state === "recording" && (
          <button
            data-testid={`${testId}-stop`}
            onClick={stop}
            className="inline-flex items-center gap-2 rounded-full bg-red-500 text-white font-semibold px-6 py-3 text-base hover:bg-red-400 animate-pulse"
          >
            <Square className="w-5 h-5" /> Stop ({secs}s)
          </button>
        )}
        {state === "uploading" && (
          <div className="inline-flex items-center gap-2 text-sm text-cyan-100">
            <Loader2 className="w-4 h-4 animate-spin" /> Transcribing…
          </div>
        )}
        {(state === "result" || state === "error") && (
          <button
            data-testid={`${testId}-reset`}
            onClick={reset}
            className="inline-flex items-center gap-1.5 rounded-full border border-white/10 bg-white/5 hover:bg-white/10 px-4 py-2 text-xs text-white"
          >
            <RotateCcw className="w-3.5 h-3.5" /> Try again
          </button>
        )}
      </div>

      {/* Recording progress bar */}
      {state === "recording" && (
        <div className="mt-3">
          <div className="h-1.5 rounded-full bg-white/5 overflow-hidden">
            <div
              className="h-full bg-red-500 transition-[width] duration-100"
              style={{ width: `${percentBar}%` }}
            />
          </div>
          <div className="mt-1 text-[10px] text-zinc-500 uppercase tracking-widest">
            Speaking will auto-stop after {Math.round(maxMs / 1000)}s
          </div>
        </div>
      )}

      {/* Result */}
      <AnimatePresence>
        {state === "result" && result && (
          <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
            className="mt-4 rounded-2xl border border-white/10 bg-black/40 p-4"
          >
            <div className="text-[10px] uppercase tracking-widest text-emerald-300 font-semibold flex items-center gap-1.5">
              {result.clarity === "clear" ? (
                <Check className="w-3.5 h-3.5" />
              ) : (
                <AlertCircle className="w-3.5 h-3.5" />
              )}
              You said · pronunciation feedback
            </div>
            <div
              data-testid={`${testId}-transcript`}
              className="mt-2 font-display text-2xl font-semibold text-white leading-tight"
            >
              {result.transcript || "(silence)"}
            </div>
            {target && (
              <>
                <div className="mt-4 text-[10px] uppercase tracking-widest text-cyan-300 font-semibold">
                  Clarity Score
                </div>
                <div className="mt-1 flex items-center gap-3">
                  <div className="flex-1 h-3 rounded-full bg-white/5 overflow-hidden">
                    <div
                      data-testid={`${testId}-clarity-bar`}
                      className="h-full rounded-full transition-[width] duration-500"
                      style={{
                        width: `${result.similarity}%`,
                        background:
                          result.clarity === "clear"
                            ? "#34d399"
                            : result.clarity === "close"
                            ? "#fbbf24"
                            : "#f87171",
                      }}
                    />
                  </div>
                  <div
                    data-testid={`${testId}-clarity-score`}
                    className="text-lg font-display font-semibold tabular-nums"
                    style={{
                      color:
                        result.clarity === "clear"
                          ? "#34d399"
                          : result.clarity === "close"
                          ? "#fbbf24"
                          : "#f87171",
                    }}
                  >
                    {result.similarity}%
                  </div>
                </div>
                <div className="mt-1 text-[10px] uppercase tracking-widest font-semibold" style={{
                  color: result.clarity === "clear" ? "#34d399" : result.clarity === "close" ? "#fbbf24" : "#f87171"
                }}>
                  {result.clarity === "clear" ? "Clear pronunciation" : result.clarity === "close" ? "Close — keep practising" : "Try again in a quiet space"}
                </div>
              </>
            )}
            <div className="mt-2 text-xs text-zinc-400">{result.tip}</div>
            <div className="mt-3 text-[10px] uppercase tracking-widest text-zinc-500">
              Non-diagnostic · task-performance only
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {state === "error" && error && (
        <div
          data-testid={`${testId}-error`}
          className="mt-3 rounded-2xl border border-red-500/30 bg-red-500/10 p-3 text-xs text-red-100"
        >
          {error}
        </div>
      )}
    </div>
  );
}
